'use client'

import Link from 'next/link'

export default function NewHomeCards() {
  return (
    <section className="relative py-16 md:py-24 bg-black">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Título de la sección */}
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 font-bisteron tracking-wider">
            ¿Qué estás buscando?
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Elige la experiencia que se ajusta a lo que quieres vivir
          </p>
        </div>

        {/* Las 2 tarjetas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto">
          {/* Tarjeta 1: Quiero volar */}
          <Link
            href="/lavidaeshoy"
            className="group relative overflow-hidden rounded-2xl border-2 border-white/20 hover:border-blue-400/60 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 aspect-[4/5] md:aspect-[16/9]"
          >
            {/* Imagen de fondo con picture/source para responsive */}
            <picture>
              <source
                media="(max-width: 640px)"
                srcSet="/images/home/parapente-volar-480.webp"
                type="image/webp"
              />
              <source
                media="(max-width: 1024px)"
                srcSet="/images/home/parapente-volar-586.webp"
                type="image/webp"
              />
              <source
                srcSet="/images/home/parapente-volar-1024.webp"
                type="image/webp"
              />
              <img
                src="/images/home/parapente-volar-1024.jpg"
                alt="Parapente volando sobre los Andes"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </picture>

            {/* Overlay azul degradado */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/85 via-blue-800/70 to-blue-900/50" />

            {/* Contenido */}
            <div className="relative h-full flex flex-col justify-between p-6 md:p-8">
              <div>
                <p className="text-blue-200 text-sm md:text-base font-semibold uppercase tracking-wider mb-2">
                  Vuelos en parapente
                </p>
                <h3 className="text-white text-3xl md:text-4xl lg:text-5xl font-bold mb-3 font-bisteron tracking-wider">
                  QUIERO VOLAR
                </h3>
                <p className="text-white/90 text-base md:text-lg mb-4 max-w-md line-clamp-3 md:line-clamp-none">
                  Vive la experiencia pura de volar sobre los Andes. Sin celebraciones, sin protocolos. Solo tú, el cielo y el viento.
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white">
                    Clásico · $255k
                  </span>
                  <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white">
                    Max · $300k
                  </span>
                  <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white">
                    Pro · $355k
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/70 text-xs uppercase tracking-wider">Desde</p>
                    <p className="text-white text-xl md:text-3xl font-bold">$255.000 COP</p>
                  </div>
                  <span
                    aria-label="Ver opciones de vuelos"
                    className="flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-white text-blue-900 shadow-lg group-hover:bg-blue-400 group-hover:text-white group-hover:scale-110 group-hover:translate-x-1 transition-all duration-300"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 md:w-7 md:h-7">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </Link>

          {/* Tarjeta 2: Es para una ocasión */}
          <div className="group relative overflow-hidden rounded-2xl border-2 border-white/20 hover:border-pink-400/60 transition-all duration-300 hover:shadow-2xl aspect-[4/5] md:aspect-[16/9]">
            {/* Imagen de fondo - selfie de paragliding (ocasión/celebración) */}
            <picture>
              <source
                media="(max-width: 640px)"
                srcSet="/images/home/parapente-ocasion-480.webp"
                type="image/webp"
              />
              <source
                media="(max-width: 1024px)"
                srcSet="/images/home/parapente-ocasion-586.webp"
                type="image/webp"
              />
              <source
                srcSet="/images/home/parapente-ocasion-1024.webp"
                type="image/webp"
              />
              <img
                src="/images/home/parapente-ocasion-1024.jpg"
                alt="Pareja disfrutando vuelo en parapente"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </picture>

            {/* Overlay rosa/bermejo degradado */}
            <div className="absolute inset-0 bg-gradient-to-r from-rose-900/85 via-pink-800/70 to-rose-900/50" />

            {/* Contenido */}
            <div className="relative h-full flex flex-col justify-between p-6 md:p-8">
              <div>
                <p className="text-pink-200 text-sm md:text-base font-semibold uppercase tracking-wider mb-2">
                  Paquetes por ocasión
                </p>
                <h3 className="text-white text-3xl md:text-4xl lg:text-5xl font-bold mb-3 font-bisteron tracking-wider">
                  ES PARA UNA OCASIÓN
                </h3>
                <p className="text-white/90 text-base md:text-lg mb-4 max-w-md line-clamp-3 md:line-clamp-none">
                  Hacemos que ese día sea inolvidable. Paquetes especiales para cumpleaños, aniversarios, propuestas y más.
                </p>
              </div>

              <div className="space-y-3">
                {/* Botones clickeables: cada uno lleva a su página */}
                <div className="flex flex-wrap gap-2 text-sm relative z-10">
                  <Link
                    href="/cumpleanos"
                    aria-label="Ver paquetes de cumpleaños"
                    className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white hover:bg-white hover:text-pink-900 transition-colors flex items-center gap-1.5 font-medium"
                  >
                    Cumpleaños
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </Link>
                  <Link
                    href="/aniversarios"
                    aria-label="Ver paquetes de aniversario"
                    className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white hover:bg-white hover:text-pink-900 transition-colors flex items-center gap-1.5 font-medium"
                  >
                    Aniversario
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </Link>
                  <Link
                    href="/propuesta"
                    aria-label="Ver paquetes de propuesta de matrimonio"
                    className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white hover:bg-white hover:text-pink-900 transition-colors flex items-center gap-1.5 font-medium"
                  >
                    Propuesta
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </Link>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/70 text-xs uppercase tracking-wider">Desde</p>
                    <p className="text-white text-xl md:text-3xl font-bold">$340.000 COP</p>
                  </div>
                  <Link
                    href="/cumpleanos"
                    aria-label="Ver todos los paquetes por ocasión"
                    className="flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-white text-pink-900 shadow-lg hover:bg-pink-400 hover:text-white hover:scale-110 hover:translate-x-1 transition-all duration-300"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 md:w-7 md:h-7">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
